import { indexOrDie, assert, mustNeverExist, type BigNumber } from "@repo/utils";
import type { ConfirmationLine, ScalarComparison, ScalarComparisonResult } from "./types.js";
import type { PurchaseOrderExtraCalculation } from "../../../../erp/types.js";

function calculateResult<T>(
    prevValue: T | null,
    nextValue: T | null,
    comparer: (a: T, b: T) => boolean,
): ScalarComparisonResult {
    let equal = false;
    if (prevValue === null && nextValue === null) {
        equal = true;
    }

    if (prevValue !== null && nextValue !== null) {
        equal = comparer(prevValue, nextValue);
    }

    return equal ? "equal" : prevValue === null ? "new" : nextValue === null ? "missing" : "different";
}

export function compareScalars<T>(
    prevValue: T | null,
    nextValue: T | null,
    comparer: (a: T, b: T) => boolean = (a, b) => a === b,
): ScalarComparison<T> {
    return {
        prevValue,
        nextValue,
        result: calculateResult(prevValue, nextValue, comparer),
    };
}

export function compareBigNumbers(a: BigNumber, b: BigNumber) {
    return a.eq(b);
}

export function compareConfirmations(a: ConfirmationLine[], b: ConfirmationLine[], skipReference = false) {
    if (a.length !== b.length) {
        return false;
    }

    for (let i = 0; i < a.length; i++) {
        const item1 = indexOrDie(a, i);
        const item2 = indexOrDie(b, i);

        if (item1.deliveryDate !== item2.deliveryDate || !item1.quantity.eq(item2.quantity)) {
            return false;
        }

        if (!skipReference && item1.reference !== item2.reference) {
            return false;
        }
    }
    return true;
}

export function compareExtraLines(
    a: Array<{ calculation: PurchaseOrderExtraCalculation }>,
    b: Array<{ calculation: PurchaseOrderExtraCalculation }>,
) {
    if (a.length !== b.length) {
        return false;
    }

    const usedIndexes = new Set<number>();

    outer: for (const item2 of b) {
        for (let i = 0; i < a.length; i++) {
            if (usedIndexes.has(i)) {
                continue;
            }

            const item1 = indexOrDie(a, i);

            if (compareExtraCalculation(item1.calculation, item2.calculation)) {
                usedIndexes.add(i);
                continue outer;
            }
        }

        return false;
    }
    return usedIndexes.size === a.length;
}

export function compareExtraCalculation(a: PurchaseOrderExtraCalculation, b: PurchaseOrderExtraCalculation) {
    if (a.class !== b.class) {
        return false;
    }

    switch (a.class) {
        case "percentage":
            assert(b.class === "percentage");
            return (
                (a.rate === null ? b.rate === null : b.rate !== null && a.rate.eq(b.rate)) &&
                a.appliedTo === b.appliedTo
            );
        case "fixedAmount":
            assert(b.class === "fixedAmount");
            return (
                (a.amount === null ? b.amount === null : b.amount !== null && a.amount.eq(b.amount)) &&
                a.distributed === b.distributed
            );
        case "quantityBased":
            assert(b.class === "quantityBased");
            return (
                a.unitPrice.eq(b.unitPrice) &&
                a.quantity.eq(b.quantity) &&
                a.perAmount.eq(b.perAmount) &&
                a.uomConversionCoefficient.eq(b.uomConversionCoefficient)
            );
        default:
            throw mustNeverExist(a);
    }
}

export function hasDeviation(comparison: ScalarComparison<unknown>) {
    return comparison.result === "different" || comparison.result === "new";
}
